import { TokenErrorStatus, TokenLinkType } from 'module/authentication/types';
import { ProductiveHeading03, BodyShort01 } from 'module/ui/components/styles';
import { Typography } from 'antd';
import { snakeCase } from 'lodash';
import { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ReactComponent as TokenExpiredIcon } from './token-error.svg';

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  padding-left: var(--spacing-12);
`;

const Wrapper = styled.div`
  width: 520px;
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  padding-bottom: var(--spacing-08);
`;

const StyledTitle = styled(Typography.Title)`
  ${ProductiveHeading03};
  font-weight: var(--heading-4-weight);
  margin: 0;
`;

const Content = styled(Typography.Paragraph)`
  font-size: var(--heading-5-size);
  font-weight: var(--heading-4-weight);
  line-height: var(--heading-5-line-height);
  margin: 0;
`;

const StyledTokenExpiredIcon = styled(TokenExpiredIcon)`
  margin: var(--layout-01) 10px;
`;
const StyledLink = styled(Typography.Link)`
  color: var(--blue-60);
  ${BodyShort01};
  letter-spacing: 0.16px;
`;

type TokenExpired = {
  email: string;
  status: TokenErrorStatus.Expired;
  resendEmail: (...args: any) => void;
  linkType?: TokenLinkType;
};

type TokenInvalid = {
  status: TokenErrorStatus.Invalid;
  email?: never;
  resendEmail?: never;
  linkType?: TokenLinkType;
};
type AccountAlreadyCreated = {
  status: TokenErrorStatus.AlreadyCompleted;
  email?: never;
  resendEmail?: never;
  linkType?: TokenLinkType;
};

type Props = TokenExpired | TokenInvalid | AccountAlreadyCreated;
type LinkProps = {
  supportEmail: string;
  loginUrl: string;
};

const getTokenExpiredBody = (linkType?: TokenLinkType) => {
  if (linkType === TokenLinkType.AccountInvitation) {
    return 'account_invite';
  }

  if (linkType === TokenLinkType.ForgotPassword) {
    return 'forgot_password';
  }

  return 'signin';
};

export const TokenError: FC<React.PropsWithChildren<Props & LinkProps>> = ({
  email,
  status,
  resendEmail,
  linkType,
  loginUrl,
  supportEmail,
}) => {
  useEffect(() => {
    resendEmail?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Center>
      <Wrapper>
        <ItemWrapper>
          <StyledTokenExpiredIcon />
          <StyledTitle>
            <FormattedMessage id="token_expired.title" />
          </StyledTitle>
        </ItemWrapper>
        <ItemWrapper>
          <Content>
            <FormattedMessage
              id={`token_expired.${snakeCase(status)}.${getTokenExpiredBody(
                linkType,
              )}.body`}
              values={{ email: <strong>{email}</strong> }}
            />
          </Content>
          <Content>
            {status !== TokenErrorStatus.AlreadyCompleted ? (
              <FormattedMessage
                id={`token_expired.${snakeCase(status)}.footer`}
                values={{
                  supportEmail: (
                    <StyledLink target="_blank" href={`mailto:${supportEmail}`}>
                      {supportEmail}
                    </StyledLink>
                  ),
                }}
              />
            ) : (
              <StyledLink href={loginUrl} rel="noreferrer">
                <FormattedMessage id="token_expired.already_completed.footer" />
              </StyledLink>
            )}
          </Content>
        </ItemWrapper>
      </Wrapper>
    </Center>
  );
};
